import {
    Button,
    Chip,
    createStyles,
    Grid,
    GridList,
    ListItem,
    ListItemText,
    Typography,
    withStyles,
    WithStyles,
} from '@material-ui/core';
import { FormikActions, getIn } from 'formik';
import * as _ from 'lodash';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import BranchKpiDialog from 'src/branchKpis/BranchKpiDialog';
import DatePickerField from 'src/common/DatePickerField/DatePickerField';
import ESDialog from 'src/common/ESDialog';
import FullWidthField from 'src/common/FullWidthField';
import IDataTableManager from 'src/common/IDataTableManager';
import SelectField from 'src/common/SelectField/SelectField';
import TypeAheadField from 'src/common/TypeAheadField/TypeAheadField';
import { IAssessment, IBranch, IKpi, IUser } from 'src/Definitions';
import { IAssessmentDto, IBranchDto, SwaggerResponse } from 'src/generated_client';
import { IStores } from 'src/Stores';
import * as Yup from 'yup';
import AssessmentDialogLogic from './AssessmentDialogLogic';

const OBSERVER_ROLE = 'Observer';
const styles = () =>
    createStyles({
        button: {
            marginTop: '7.5px',
        },
        formContainer: {
            paddingTop: '10px',
        },
        gridList: {
            height: 'auto',
        },
        relatedAssessments: {
            border: '1px',
            borderColor: 'lightgray',
            borderRadius: '4px',
            borderStyle: 'solid',
            maxHeight: '128px',
            maxWidth: '100%',
            overflowX: 'hidden',
            overflowY: 'auto',
        },
    });

interface IAssessmentFormValues {
    name: string;
    visitStartedOn: moment.Moment;
    visitEndedOn: moment.Moment | undefined;
    observerIds: string[];
    improvementStrategy: string;
    improvementGoals: string;
    history: string;
    kpiIds: string[];
    comparisonBranchIds: string[];
    targetBranchId: string;
    dueDate: moment.Moment | undefined;
}

const AssessmentSchema = Yup.object().shape({
    assessment: Yup.object().shape({}),
    improvementGoals: Yup.string().required('Improvement Goals are required'),
    improvementStrategy: Yup.string().required('Improvement Strategy is required'),
    name: Yup.string().required('Name is required'),
    observerIds: Yup.string().required('At least one Observer required'),
    targetBranchId: Yup.string().required('Target Branch is required'),
    visitStartedOn: Yup.string().required('Start Date is required'),
});

interface IAssessmentDialogProps extends WithStyles<typeof styles> {
    assessment?: IAssessment;
    close?: () => void;
    dataTableManager?: IDataTableManager<IAssessment>;
    getAssessmentsByTargetBranchId?: (
        targetBranch: IBranchDto,
        callback: (response: SwaggerResponse<IAssessmentDto[]>) => void
    ) => Promise<void>;
    getBranches?: (pageNumber?: number, pageSize?: number) => Promise<void>;
    getKpis?: (pageNumber?: number, pageSize?: number) => Promise<void>;
    getUsers?: (pageNumber?: number, roleName?: string) => Promise<void>;
    isLoading?: boolean;
    isOpen?: boolean;
    openBranchKpiDialog?: (targetBranch: IBranch, branches: IBranch[], kpis: IKpi[]) => void;
    openDialogWithAssessment?: (assessment: IAssessment) => void;
    saveAssessment?: (assessment: IAssessment) => Promise<void>;
    users?: IUser[];
    branches?: IBranch[];
    kpis?: IKpi[];
    rerouteToPath?: (path: string) => void;
}

class AssessmentDialog extends React.Component<IAssessmentDialogProps> {
    private _logic: AssessmentDialogLogic = new AssessmentDialogLogic();
    private ASSESSMENT_COLUMN_NUMBER = 1;
    private HEIGHT_OF_ASSESSMENTS = 30;
    public state = {
        targetBranchAssessments: new Array<IAssessment>(),
    };

    public componentDidMount() {
        this.props.getBranches!(undefined, 5000);
        this.props.getUsers!(undefined, OBSERVER_ROLE);
        this.props.getKpis!(undefined, 5000);
        if (!this.props.assessment!.isNew) {
            this.props.getAssessmentsByTargetBranchId!(
                this.props.assessment!.targetBranch,
                this.setTargetBranchAssessments
            ).then(() => this.getBranchAssessmentsTableManager());
        }
    }

    public setTargetBranchAssessments = (response: SwaggerResponse<IAssessmentDto[]>) => {
        this.setState({ targetBranchAssessments: response.result });
    };

    public getBranchAssessmentsTableManager() {
        _.remove(this.state.targetBranchAssessments, (x) => {
            return (
                x.targetBranchName !== this.props.assessment!.targetBranchName ||
                x.id === this.props.assessment!.id
            );
        });
    }

    public openRelatedAssessment = (assessment: IAssessment) => {
        return () => {
            this.props.close!();
            this.props.openDialogWithAssessment!(assessment);
        };
    };

    public save = (
        values: IAssessmentFormValues,
        formikBag: FormikActions<IAssessmentFormValues>
    ) => {
        const assessmentToSave = {
            ...this.props.assessment,
            dueDate: values.dueDate ? moment(values.dueDate).toDate() : undefined,
            history: values.history,
            id: this.props!.assessment!.id,
            improvementGoals: values.improvementGoals,
            improvementStrategy: values.improvementStrategy,
            name: values.name,
            visitEndedOn: values.visitEndedOn ? moment(values.visitEndedOn).toDate() : undefined,
            visitStartedOn: values.visitStartedOn
                ? moment(values.visitStartedOn).toDate()
                : undefined,
        } as IAssessment;

        this._logic
            .save(
                assessmentToSave,
                values,
                this.props.users!,
                this.props.kpis!,
                this.props.branches!,
                this.props.saveAssessment!
            )
            .finally(() => formikBag.setSubmitting(false));
    };

    public getComparisonBranchIds() {
        if (this.props.assessment && this.props.assessment.comparisonBranches) {
            return this.props.assessment.comparisonBranches.map((x) => x.id) as string[];
        } else {
            return [];
        }
    }

    public getKpiIds() {
        if (this.props.assessment && this.props.assessment.kpis) {
            return this.props.assessment.kpis.map((x) => x.id) as string[];
        } else {
            return [];
        }
    }

    public getObserverIds() {
        if (this.props.assessment && this.props.assessment.observers) {
            return this.props.assessment.observers.map((x) => x.id) as string[];
        } else {
            return [];
        }
    }

    public getTargetBranchId() {
        if (this.props.assessment && this.props.assessment.targetBranch) {
            return this.props.assessment.targetBranch.id || '';
        } else {
            return '';
        }
    }

    public getObserverName(item: IUser) {
        return item.name || '';
    }

    public getObserverValue(item: IUser) {
        return item.id || '';
    }

    public getBranchName(item: IBranch) {
        return item.name || '';
    }

    public getBranchValue(item: IBranch) {
        return item ? item.id || '' : '';
    }

    public getBranchRegion(targetBranchId: string) {
        const targetBranch = _.find(this.props.branches, { id: targetBranchId });
        if (targetBranch) {
            return targetBranch.regionName;
        } else {
            return '';
        }
    }

    private getBranches(): IBranch[] {
        return this.props.branches ? this.props.branches : [];
    }

    private getSelectedKpis(values: IAssessmentFormValues) {
        const kpis = this.props.kpis ? this.props.kpis : [];
        return this._logic.getSelectedKpis(values.kpiIds, kpis);
    }

    private getInitialTargetBranch(initialValues: IAssessmentFormValues) {
        return this._logic.findBranch({ id: initialValues.targetBranchId }, this.getBranches());
    }

    private getSelectedTargetBranch(values: IAssessmentFormValues) {
        return this._logic.findBranch({ id: values.targetBranchId }, this.getBranches());
    }

    private getValidTargetBranches(values: IAssessmentFormValues) {
        return this._logic.getValidTargetBranches(values.comparisonBranchIds, this.getBranches());
    }

    private getInitialComparisonBranches(initialValues: IAssessmentFormValues) {
        return this._logic.getBranchesForIds(initialValues.comparisonBranchIds, this.getBranches());
    }

    public getSelectedComparisonBranches(values: IAssessmentFormValues) {
        return this._logic.getBranchesForIds(values.comparisonBranchIds, this.getBranches());
    }

    public getValidComparisonBranches(values: IAssessmentFormValues) {
        return this._logic.getValidComparisonBranches(values.targetBranchId, this.getBranches());
    }

    public getKpiName(item: IKpi) {
        return item.name || '';
    }

    public getKpiValue(item: IKpi) {
        return item.id || '';
    }

    public render() {
        const { close, isLoading, isOpen, assessment, users, branches, kpis } = this.props;
        const initialValues = {
            comparisonBranchIds: this.getComparisonBranchIds(),
            dueDate: assessment!.dueDate ? moment(assessment!.dueDate) : undefined,
            history: assessment!.history,
            improvementGoals: assessment!.improvementGoals,
            improvementStrategy: assessment!.improvementStrategy,
            kpiIds: this.getKpiIds(),
            name: assessment!.name,
            observerIds: this.getObserverIds(),
            targetBranchId: this.getTargetBranchId(),
            visitEndedOn: assessment!.visitEndedOn ? moment(assessment!.visitEndedOn) : undefined,
            visitStartedOn: moment(assessment!.visitStartedOn) || moment(),
        };

        const hasValue = (field: string, values: IAssessmentFormValues) => {
            const value = getIn(values, field);

            return !!value;
        };

        return (
            <>
                <ESDialog
                    close={close!}
                    initialValues={initialValues}
                    isLoading={isLoading}
                    open={isOpen!}
                    save={this.save}
                    title={assessment!.isNew ? 'Create Assessment' : 'Edit Assessment'}
                    validationSchema={AssessmentSchema}
                    validateOnBlur={false}
                    validateOnChange={true}
                >
                    {({ errors, values, setFieldValue }) => (
                        <Grid container spacing={24} className={this.props.classes.formContainer}>
                            <Grid item sm={12} md={3}>
                                {this.setAssessmentName(values, setFieldValue)}
                                <FullWidthField
                                    autoFocus
                                    name="name"
                                    label="Name Of Assessment"
                                    required
                                />
                            </Grid>
                            <Grid item sm={12} md={3}>
                                <DatePickerField
                                    clearable={true}
                                    name="dueDate"
                                    label="Due Date"
                                    inputId="dueDate"
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} md={3}>
                                {branches && (
                                    <TypeAheadField
                                        outlined
                                        label="Target Branch Search"
                                        name="targetBranchId"
                                        inputId="targetBranchId"
                                        cypressLabel="targetBranches"
                                        childCypressLabel="targetBranch"
                                        errorMessage={errors.targetBranchId}
                                        fullWidth
                                        setFieldValue={setFieldValue}
                                        initialValue={this.getInitialTargetBranch(initialValues)}
                                        selectedValue={this.getSelectedTargetBranch(values)}
                                        items={this.getValidTargetBranches(values)}
                                        getName={this.getBranchName}
                                        getValue={this.getBranchValue}
                                        required
                                    />
                                )}
                            </Grid>
                            <Grid item sm={12} md={3}>
                                <Typography variant="body2" component="h2">
                                    Region:
                                </Typography>
                                <Typography variant="body2" component="h2">
                                    {this.getBranchRegion(values.targetBranchId)}
                                </Typography>
                            </Grid>
                            <Grid item sm={12} md={3}>
                                <DatePickerField
                                    name="visitStartedOn"
                                    label="Visit Start Date"
                                    inputId="visitStartedOn"
                                    errorMessage={errors.visitStartedOn}
                                    required
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} md={3}>
                                <DatePickerField
                                    clearable={true}
                                    name="visitEndedOn"
                                    inputId="visitEndedOn"
                                    label="Visit End Date"
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} md={3}>
                                {branches && (
                                    <TypeAheadField
                                        multiple
                                        outlined
                                        label="Comparison Branches Search"
                                        name="comparisonBranchIds"
                                        inputId="comparisonBranchIds"
                                        cypressLabel="comparisonBranches"
                                        childCypressLabel="comparisonBranch"
                                        fullWidth
                                        setFieldValue={setFieldValue}
                                        initialValue={this.getInitialComparisonBranches(
                                            initialValues
                                        )}
                                        selectedValue={this.getSelectedComparisonBranches(values)}
                                        items={this.getValidComparisonBranches(values)}
                                        getName={this.getBranchName}
                                        getValue={this.getBranchValue}
                                        hideChips
                                    />
                                )}
                            </Grid>
                            <Grid item sm={12} md={3}>
                                <Typography variant="body2" component="h2">
                                    Comparison Branches:
                                </Typography>
                                {this.renderComparisonBranchChips(
                                    values.comparisonBranchIds,
                                    setFieldValue,
                                    values
                                )}
                            </Grid>
                            <Grid item sm={12} md={5}>
                                <SelectField
                                    cypressLabel="observers"
                                    childCypressLabel="observer"
                                    errorMessage={errors.observerIds}
                                    inputId="observerIds"
                                    getName={this.getObserverName}
                                    getValue={this.getObserverValue}
                                    items={users}
                                    label="Observers (at least one required)"
                                    name="observerIds"
                                    checkedItems={values.observerIds}
                                    multiple
                                    required
                                    fullWidth
                                    outlined
                                    shrinkLabel={hasValue('observerIds', values)}
                                />
                            </Grid>
                            <Grid item sm={8} md={5}>
                                <SelectField
                                    cypressLabel="kpis"
                                    childCypressLabel="kpi"
                                    inputId="kpiIds"
                                    getName={this.getKpiName}
                                    getValue={this.getKpiValue}
                                    items={kpis}
                                    label="KPI Measurements"
                                    name="kpiIds"
                                    checkedItems={values.kpiIds}
                                    multiple
                                    fullWidth
                                    outlined
                                    shrinkLabel={hasValue('kpiIds', values)}
                                />
                            </Grid>
                            <Grid item sm={4} md={2}>
                                <Button
                                    className={this.props.classes.button}
                                    variant="contained"
                                    disabled={this.disableBranchKpis(
                                        this.getSelectedTargetBranch(values),
                                        this.getSelectedKpis(values),
                                        values,
                                        initialValues
                                    )}
                                    onClick={this.openDialog(
                                        this.getSelectedTargetBranch(values),
                                        this.getSelectedComparisonBranches(values),
                                        this.getSelectedKpis(values)
                                    )}
                                >
                                    {'VALUES'}
                                </Button>
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <FullWidthField
                                    rows="6"
                                    multiline={true}
                                    autoFocus
                                    name="improvementStrategy"
                                    label="Improvement Strategy"
                                    variant="outlined"
                                    required
                                />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <FullWidthField
                                    rows="6"
                                    autoFocus
                                    multiline={true}
                                    name="improvementGoals"
                                    label="Improvement Goals"
                                    variant="outlined"
                                    required
                                />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <Typography variant="body2" component="h2">
                                    Other Assessments for Target Branch:
                                </Typography>
                                <div className={this.props.classes.relatedAssessments}>
                                    <GridList
                                        cols={this.ASSESSMENT_COLUMN_NUMBER}
                                        cellHeight={this.HEIGHT_OF_ASSESSMENTS}
                                        className={this.props.classes.gridList}
                                    >
                                        {this.state.targetBranchAssessments.map(
                                            (targetBranchAssessment) => {
                                                return (
                                                    <ListItem
                                                        key={targetBranchAssessment.name}
                                                        button
                                                        dense
                                                        divider
                                                        onClick={this.openRelatedAssessment(
                                                            targetBranchAssessment
                                                        )}
                                                    >
                                                        <ListItemText
                                                            primary={targetBranchAssessment.name}
                                                            primaryTypographyProps={{
                                                                noWrap: true,
                                                            }}
                                                        />
                                                    </ListItem>
                                                );
                                            }
                                        )}
                                    </GridList>
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </ESDialog>
                <BranchKpiDialog />
            </>
        );
    }

    public disableBranchKpis(
        targetBranch: IBranch | undefined,
        kpis: IKpi[],
        initialValues: IAssessmentFormValues,
        values: IAssessmentFormValues
    ) {
        return !(
            targetBranch &&
            kpis &&
            !this.props.assessment!.isNew &&
            kpis.length > 0 &&
            this.kpisHaveNotChanged(values.kpiIds, initialValues.kpiIds) &&
            this.comparisonBranchesHaveNotChanged(
                values.comparisonBranchIds,
                initialValues.comparisonBranchIds
            ) &&
            values.targetBranchId === initialValues.targetBranchId
        );
    }

    private kpisHaveNotChanged(kpisValue: string[], kpisInitialValue: string[]) {
        return this._logic.areIdsSameAsInitialValues(kpisInitialValue, kpisValue);
    }

    private comparisonBranchesHaveNotChanged(
        branchesValue: string[],
        branchesInitialValue: string[]
    ) {
        return this._logic.areIdsSameAsInitialValues(branchesInitialValue, branchesValue);
    }

    private openDialog = (targetBranch: IBranch | undefined, branches: IBranch[], kpis: IKpi[]) => {
        if (targetBranch && kpis && !this.props.assessment!.isNew) {
            return () => {
                this.props.openBranchKpiDialog!(targetBranch, branches, kpis);
            };
        }
        return () => {};
    };

    private renderComparisonBranchChips(
        branchIds: string[],
        setFieldValue: (name: string, value: any) => void,
        values: IAssessmentFormValues
    ) {
        if (!branchIds) {
            return null;
        }
        return this._logic.getBranchesForIds(branchIds, this.getBranches()).map((branch) => {
            return (
                <Chip
                    key={branch.id}
                    label={branch.name}
                    onDelete={this.handleBranchDelete(branch, setFieldValue, values)}
                />
            );
        });
    }

    private handleBranchDelete = (
        branchToDelete: IBranch,
        setFieldValue: (name: string, value: any) => void,
        values: IAssessmentFormValues
    ) => () => {
        setFieldValue(
            'comparisonBranchIds',
            values.comparisonBranchIds.filter((id) => id !== branchToDelete.id)
        );
    };

    private setAssessmentName(
        values: IAssessmentFormValues,
        setFieldValue: (name: string, value: any) => void
    ) {
        this._logic.setAssessmentName(values, this.getBranches(), setFieldValue);
        return undefined;
    }
}

const InjectedAssessmentDialog = inject<
    IStores,
    IAssessmentDialogProps,
    Partial<IAssessmentDialogProps>,
    any
>((stores: IStores) => ({
    assessment: stores.assessments.selectedAssessment,
    branches: stores.branches.branches,
    close: stores.assessments.closeDialog,
    dataTableManager: stores.assessments.dataTableStore,
    getAssessmentsByTargetBranchId: stores.assessments.getAssessmentsByTargetBranchId,
    getBranches: stores.branches.getAllBranches,
    getKpis: stores.kpis.getAllKpis,
    getUsers: stores.users.getAllUsers,
    isLoading: stores.assessments.isLoading,
    isOpen: stores.assessments.isModalOpen,
    kpis: stores.kpis.kpis,
    openBranchKpiDialog: stores.branchKpis.openBranchKpiDialog,
    openDialogWithAssessment: stores.assessments.openDialogWithAssessment,
    rerouteToPath: stores.global.rerouteToPath,
    saveAssessment: stores.assessments.saveAssessment,
    users: stores.users.users,
}))(withStyles(styles)(AssessmentDialog));

export default InjectedAssessmentDialog;
export { IAssessmentFormValues };



// WEBPACK FOOTER //
// ./src/assessments/AssessmentDialog.tsx